import { useState, useEffect } from "react"
import { getAuth } from "firebase/auth"
import { doc, getDoc } from "firebase/firestore"
import { db } from "@/utils/firebase"
import { useFirebase } from "@/context/FirebaseProvider"
import { BusinessRole, businessRoleEnum } from "@/schemas/business" // Use Zod-inferred BusinessRole and its enum

export const useBusinessCardRole = (
  businessId: string,
  defaultRole: BusinessRole // This is now Zod-inferred BusinessRole
): BusinessRole => { // Ensure return type matches
  const { businesses: allBusinessesFromFirebase } = useFirebase()
  const auth = getAuth()
  const currentUserId = auth.currentUser?.uid
  const [memberRole, setMemberRole] = useState<BusinessRole>(defaultRole) // Initialize with defaultRole

  useEffect(() => {
    const fetchMemberRole = async () => {
      if (!currentUserId || !businessId) {
        setMemberRole(defaultRole); // Set to default if no user or businessId
        return;
      }

      try {
        // Attempt 1: Direct Firestore fetch for the business document
        const businessDocRef = doc(db, "businesses", businessId)
        const businessSnap = await getDoc(businessDocRef)

        if (businessSnap.exists()) {
          const businessData = businessSnap.data()
          const members = businessData?.members || []
          const currentMember = members.find(
            (m: any) => m.userId === currentUserId
          )

          if (currentMember && currentMember.role) {
            // Validate if currentMember.role is a valid BusinessRole
            if (businessRoleEnum.safeParse(currentMember.role).success) {
              setMemberRole(currentMember.role as BusinessRole);
              return;
            }
          }
        }

        // Attempt 2: Check businesses from FirebaseProvider (covers businesses user has access to)
        const businessFromFirebase = allBusinessesFromFirebase?.find(b => b.id === businessId);
        if (businessFromFirebase?.members) {
          const currentMemberInBusiness = businessFromFirebase.members.find(
            (m) => m.userId === currentUserId // m is BusinessMemberZod here
          );
          if (currentMemberInBusiness && currentMemberInBusiness.role) {
            // currentMemberInBusiness.role is BusinessRole | null | undefined from Zod schema
            if (businessRoleEnum.safeParse(currentMemberInBusiness.role).success) {
              setMemberRole(currentMemberInBusiness.role as BusinessRole); // Cast needed because role can be null/undefined
              return;
            }
          }
        }
        
        // Fallback to defaultRole if no specific role found
        // Ensure defaultRole itself is a valid BusinessRole enum member
        if (businessRoleEnum.safeParse(defaultRole).success) {
          setMemberRole(defaultRole);
        } else {
          setMemberRole("Manager"); // Ultimate fallback
        }

      } catch (error) {
        console.error("Error fetching member role for business:", businessId, error)
        if (businessRoleEnum.safeParse(defaultRole).success) {
          setMemberRole(defaultRole); // Fallback to defaultRole on error
        } else {
          setMemberRole("Manager"); // Ultimate fallback
        }
      }
    }

    fetchMemberRole()
  }, [businessId, currentUserId, defaultRole, allBusinessesFromFirebase])

  return memberRole
}
